import { Column, RawColumn, Section } from '@miq/catalog';

/*
 ************************************************************************************
 * This files contains helper functions related to catalog matters(Section, Column) *
 * find, update, filter etc.                                                        *
 ************************************************************************************
 */

/*
 ****************************************************
 **************  COLUMNS HELPERS ********************
 ****************************************************
 */

/**
 * Simple properties found on a column object
 */
export type ColumnSimpleKeys =
  | 'columnId'
  | 'header'
  | 'field'
  | 'tooltip'
  | 'dataType'
  | 'lookupCategory'
  | 'inputType';

/**
 * Return found column for passed columnId if found, else returns undefined
 * @param columns
 * @param targetColumnId
 */
export const findColumnBy = <T extends RawColumn = Column>(
  columns: T[],
  key: ColumnSimpleKeys,
  targetValue: number | string | boolean
) => {
  return columns.find(column => column[key] === targetValue);
};

/**
 * Return found column for passed columnId if found, else returns undefined
 * @param columns
 * @param targetColumnId
 */
export const findColumnById = <T extends RawColumn = Column>(columns: T[], targetColumnId: number) => {
  return findColumnBy(columns, 'columnId', targetColumnId);
};

/**
 * Return found columns for passed columnId set if found, else returns `[]`
 * @param columns
 * @param targetColumnsIds
 */
export const filterColumnsByIds = <T extends RawColumn = Column>(columns: T[], targetColumnsIds: number[]) => {
  return columns.filter(({ columnId }) => targetColumnsIds.includes(columnId));
};

/**
 * Search and return a single columns that matches the provided column key and value.
 *
 * @param sections MIQ catalog array
 * @param key Object key to use for comparison
 * @param value Value to use for comparison
 */
export function findColumnInSectionsBy<T extends Section = Section<any>>(
  sections: T[],
  key: ColumnSimpleKeys,
  value: string | number | boolean
) {
  let i = 0;
  while (i < sections.length) {
    const columnFound = findColumnBy(sections[i].columns, key, value);
    if (!!columnFound) {
      return columnFound;
    }
    i++;
  }
  return null;
}

/**
 * Wrapper function to find a column exclusively by field.
 *
 * @param catalog MIQ catalog array
 * @param value Value to use for comparison
 */
export function findColumnInSectionsByField<T extends Section = Section>(catalog: T[], value: string) {
  return findColumnInSectionsBy(catalog, 'field', value);
}

/**
 * Mutates the column configuration for given column field name using columnChange (Partial<Column>)
 *
 * @param catalog {RecordDetailInput[]}
 * @param targetField {string}
 * @param columnChange {Partial<Column>}
 */
export function updateColumnByField<T extends Section = Section>(
  catalog: T[],
  targetField: string,
  columnChange: Partial<Column>
) {
  const column = findColumnInSectionsBy(catalog, 'field', targetField);
  return Object.assign(column, columnChange);
}

/*
 ****************************************************
 **************  SECTIONS BASED HELPERS *******************
 ****************************************************
 */
/**
 * Return found section for passed targetSectionId if found, else returns unknown section
 * @param sections
 * @param targetSectionId
 */
export const findSectionById = <T extends { sectionId?: number } = Section>(sections: T[], targetSectionId: number) => {
  return sections.find(({ sectionId }) => sectionId === targetSectionId);
};
